import { Mui, MuiStyles, MuiIcons } from "@osu/react-ui";
import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { selectSearchData } from "../../services/graphql/slices/search-slice";
import { selectExportsData } from "../../services/graphql/slices/exports-slice";
import useTableStyles from '../../Common/tableStyles';
import { MaterialReactTable } from 'material-react-table';
import { ToolbarInternalActionsWithReset } from "../../Common/ToolbarInternalActionsWithReset";
import { addSpacesToCamelCase } from '../../util/functions';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'; // eslint-disable-line
import ChangeWorkflowDialog from "./ChangeWorkflowDialog";
import DialogAdminOverride from "./DialogAdminOverride";
import ChangeRequestedDialog from "./ChangeRequestedDialog";
import useRoleAuthentication from "../../Authentication/hooks/useRoleAuthentication";
import { APPLICATION_STATUS, TOOLTIP_STATUS, TOOLTIPS, STUDENT_REQUEST_ACTIONS } from "../../util/constants";
import { useLazyExportGridQuery } from "../../services/graphql/api";
import EasyCopy from "../../Common/EasyCopy";

function ResultsGrid({ searchParams, clearSearchCriteria, clearGrid, isPersonSearch = false, paginationModel = { pageIndex: 0, pageSize: 100 }, sortModel = [{ id: 'lastName', desc: false }], setPaginationModel, setSortModel }) {
    const theme = MuiStyles.useTheme();
    const { sharedTableStyles } = useTableStyles();
    const resultData = useSelector(selectSearchData);
    const isLoading = resultData?.isLoading || false;
    const [anchorEl, setAnchorEl] = useState(null); // eslint-disable-line
    const [openDialog, setOpenDialog] = useState(false);
    const [openOverrideDialog, setOpenOverrideDialog] = useState(false);
    const [openProgramDialog, setOpenProgramDialog] = useState(false);
    const authDetails = useRoleAuthentication();
    const isAdmin = authDetails?.isAnyAdmin;
    const isHonorsSeasonalManager = authDetails?.isHonorsSeasonalManager;

    const [selectedRows, setSelectedRows] = useState({});
    const [triggerExport, { isLoading: isExporting }] = useLazyExportGridQuery();
    const exportsData = useSelector(selectExportsData);
    const [pendingExportReceiptId, setPendingExportReceiptId] = useState(null);

    // localStorage persistence for column settings
    const storageKey = isPersonSearch ? 'search-results-person' : 'search-results-advanced';
    const [columnOrder, setColumnOrder] = useState([]);
    const previousColumnOrderRef = useRef([]);
    const previousColumnIdsRef = useRef([]);

    // Monitor exports state to clear pending state when export completes
    useEffect(() => {
        console.log("ResultsGrid - exports state changed:", exportsData);
        // If we have a pending export and it's no longer in the exports array, clear the pending state
        if (pendingExportReceiptId && !exportsData.exports.find(exp => exp.receiptId === pendingExportReceiptId)) {
            console.log("Export completed, clearing pending state");
            setPendingExportReceiptId(null);
        }
    }, [exportsData, pendingExportReceiptId]);

    // Memoize the columns directly from resultData without cloning
    // Filter out columns with periods (nested fields) - those will go in detail panel
    const mappedColumns = useMemo(() => {
        if (!resultData?.columns) return [];

        const mapped = resultData.columns
            .filter(col => !col.field.includes('.')) // Exclude nested fields from main columns
            .map(col => {
                const baseColumn = {
                    ...col,
                    id: col.field,
                    header: col.headerName,
                    accessorKey: col.field,
                };

                // Make the id column a link to applicant detail page
                if (col.field === 'applicantEmployeeId') {
                    baseColumn.Cell = ({ row }) => {
                        const applicantEmployeeId = row.original.applicantEmployeeId;
                        const programId = row.original.programId;
                        const term = row.original.term;
                        const studentRequestIsActive = row.original.studentRequestIsActive === "Yes";
                        const studentRequestType = row.original.studentRequestType;
                        const studentRequestApproved = row.original.studentRequestApproved === "Yes";
                        const isPendingProcessing = row.original.isPendingProcessing === "Yes";

                        const tooltips = [];
                        if (studentRequestIsActive) {
                            if (studentRequestType === STUDENT_REQUEST_ACTIONS.APPEAL.label) {
                                tooltips.push(TOOLTIPS(TOOLTIP_STATUS.APPEAL_REQUESTED));
                            } else if (studentRequestType === STUDENT_REQUEST_ACTIONS.PROGRAM_CHANGE.label) {
                                tooltips.push(TOOLTIPS(TOOLTIP_STATUS.PROGRAM_CHANGE_REQUESTED));
                            } else if (studentRequestType === STUDENT_REQUEST_ACTIONS.WITHDRAW.label) {
                                tooltips.push(TOOLTIPS(TOOLTIP_STATUS.WITHDRAW_REQUESTED));
                            }
                        }
                        if (studentRequestApproved) {
                            tooltips.push(TOOLTIPS(TOOLTIP_STATUS.STUDENT_REQUEST_APPROVED));
                        }
                        if (isPendingProcessing) {
                            tooltips.push(TOOLTIPS(TOOLTIP_STATUS.PENDING_PROCESSING));
                        }

                        if (applicantEmployeeId && programId && term) {
                            if (isAdmin || isHonorsSeasonalManager) {
                                const detailUrl = `/applicant/${applicantEmployeeId}/${programId}/${term}`;
                                return (
                                    <>
                                        <EasyCopy contents={applicantEmployeeId} />
                                        <Link
                                            to={detailUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: 'inherit',
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {applicantEmployeeId}
                                            {tooltips}
                                        </Link>
                                    </>
                                );
                            }
                        }

                        return ( <><EasyCopy contents={applicantEmployeeId} /> {applicantEmployeeId} {tooltips} </> );
                    };
                }

                return baseColumn;
            });

        return mapped;
    }, [resultData?.columns, isAdmin, isHonorsSeasonalManager]);

    // Get nested field columns for detail panel
    // Group by parent field to match backend deduplication
    const nestedFieldColumns = useMemo(() => {
        if (!resultData?.columns) return [];

        const nestedCols = resultData.columns.filter(col => col.field.includes('.'));

        // Group nested columns by their parent field (e.g., "commentCodes.category" -> "commentCodes")
        const grouped = new Map();
        nestedCols.forEach(col => {
            const parentField = col.field.split('.')[0];
            if (!grouped.has(parentField)) {
                grouped.set(parentField, {
                    parentField,
                    headerName: parentField.charAt(0).toUpperCase() + parentField.slice(1), // Capitalize first letter
                    originalColumns: []
                });
            }
            grouped.get(parentField).originalColumns.push(col);
        });

        return Array.from(grouped.values());
    }, [resultData?.columns]);

    const baseColumnIds = useMemo(() => mappedColumns.map(col => col.id), [mappedColumns]);
    const defaultColumnOrder = useMemo(() => {
        const extras = [];
        if (nestedFieldColumns.length > 0) { extras.push("mrt-row-expand"); }
        if (isPersonSearch) { extras.push("mrt-row-select"); }
        return [...extras, ...baseColumnIds];
    }, [baseColumnIds, nestedFieldColumns.length, isPersonSearch]);

    // Calculate default (API/criteria) visibility and saved-overlay initial visibility
    const defaultColumnVisibility = useMemo(() => {
        if (!resultData?.columns) return {};

        const criteriaFields = new Set();
        if (searchParams?.criteria && Array.isArray(searchParams.criteria)) {
            searchParams.criteria.forEach(criterion => {
                if (criterion.attribute) {
                    criteriaFields.add(criterion.attribute);
                }
            });
        }

        const visibility = {};
        resultData.columns.forEach(col => {
            if (criteriaFields.has(col.field)) {
                visibility[col.field] = true;
            } else {
                visibility[col.field] = col.default === true;
            }
        });
        return visibility;
    }, [resultData?.columns, searchParams?.criteria]);

    const initialColumnVisibility = useMemo(() => {
        if (!resultData?.columns) return {};

        const saved = localStorage.getItem(storageKey);
        let savedSettings = null;
        if (saved) {
            try {
                savedSettings = JSON.parse(saved);
            } catch (e) {
                console.warn('Failed to parse saved settings');
            }
        }

        const criteriaFields = new Set();
        if (searchParams?.criteria && Array.isArray(searchParams.criteria)) {
            searchParams.criteria.forEach(criterion => {
                if (criterion.attribute) {
                    criteriaFields.add(criterion.attribute);
                }
            });
        }

        const visibility = { ...defaultColumnVisibility };
        if (savedSettings?.columnVisibility) {
            Object.entries(savedSettings.columnVisibility).forEach(([id, val]) => {
                // Don't let saved settings hide columns that are in the current search criteria
                if (criteriaFields.has(id)) return;
                visibility[id] = val;
            });
        }
        return visibility;
    }, [defaultColumnVisibility, resultData?.columns, storageKey, searchParams?.criteria]);

    const [columnVisibility, setColumnVisibility] = useState(initialColumnVisibility);
    const initialColumnVisibilityRef = useRef(initialColumnVisibility);
    const defaultColumnVisibilityRef = useRef(defaultColumnVisibility);

    // Update state when the set of columns changes; avoid overwriting user-changed order for the same column set
    useEffect(() => {
        const currentColumnIds = defaultColumnOrder;
        const previousColumnIdsKey = previousColumnIdsRef.current.join(',');
        const currentColumnIdsKey = currentColumnIds.join(',');
        const columnsChanged = currentColumnIdsKey !== previousColumnIdsKey;
        
        const visibilityChanged = JSON.stringify(defaultColumnVisibility) !== JSON.stringify(defaultColumnVisibilityRef.current);
        const initialVisibilityChanged = JSON.stringify(initialColumnVisibility) !== JSON.stringify(initialColumnVisibilityRef.current);
        if (!columnsChanged && !visibilityChanged && !initialVisibilityChanged) return;

        setColumnVisibility(initialColumnVisibility);
        initialColumnVisibilityRef.current = initialColumnVisibility;
        defaultColumnVisibilityRef.current = defaultColumnVisibility;

        const saved = localStorage.getItem(storageKey);
        let savedOrder = [];
        if (saved) {
            try {
                const savedSettings = JSON.parse(saved);
                if (Array.isArray(savedSettings?.columnOrder)) {
                    savedOrder = savedSettings.columnOrder;
                }
            } catch (e) {
                console.warn('Failed to parse saved column order');
            }
        }

        const allowedIds = new Set(defaultColumnOrder);
        const filteredSaved = savedOrder.filter((id) => allowedIds.has(id));
        const missing = defaultColumnOrder.filter((id) => !filteredSaved.includes(id));
        const nextOrder = filteredSaved.length > 0 ? [...filteredSaved, ...missing] : defaultColumnOrder;

        setColumnOrder(nextOrder);
        previousColumnOrderRef.current = nextOrder;
        previousColumnIdsRef.current = currentColumnIds;
    }, [defaultColumnOrder, defaultColumnVisibility, initialColumnVisibility, storageKey]);

    useEffect(() => {
        initialColumnVisibilityRef.current = initialColumnVisibility;
        defaultColumnVisibilityRef.current = defaultColumnVisibility;
    }, [initialColumnVisibility, defaultColumnVisibility]);

    // Ensure criteria columns are always visible when a search is performed
    useEffect(() => {
        if (!searchParams?.criteria || !Array.isArray(searchParams.criteria)) return;

        setColumnVisibility(prev => {
            const updated = { ...prev };
            let changed = false;
            searchParams.criteria.forEach(criterion => {
                if (criterion.attribute && updated[criterion.attribute] === false) {
                    updated[criterion.attribute] = true;
                    changed = true;
                }
            });
            return changed ? updated : prev;
        });
    }, [searchParams?.criteria]);

    console.log("initialColumnVisibility:", initialColumnVisibility);

    const persistColumnVisibility = useCallback((nextVisibilityOrUpdater) => {
        setColumnVisibility((prev) => {
            const resolved = typeof nextVisibilityOrUpdater === 'function' ? nextVisibilityOrUpdater(prev) : nextVisibilityOrUpdater;
            console.log("Persisting column visibility:", resolved);
            const saved = localStorage.getItem(storageKey);
            const existing = saved ? JSON.parse(saved) : {};
            localStorage.setItem(storageKey, JSON.stringify({ ...existing, columnVisibility: resolved }));
            return resolved;
        });
    }, [storageKey]);

    // Memoize the table data directly from resultData
    const tableData = useMemo(() => {
        if (!resultData?.results) return [];

        return resultData.results.map((row, idx) => ({
                id: row.id ?? idx,
                ...row,
        }));
    }, [resultData?.results]);

    const handleDropdownClick = (event) => { // eslint-disable-line
        setAnchorEl(event.currentTarget);
    };

    const handleDropdownClose = () => {
        setAnchorEl(null);
    };

    const handleChangeWorkflow = () => { // eslint-disable-line
        handleDropdownClose();
        setOpenDialog(true);
    };

    const handleOverride = () => { // eslint-disable-line
        handleDropdownClose();
        setOpenOverrideDialog(true);
    };

    // Function to clear all selected rows
    const handleClearSelection = () => {
        setSelectedRows({});
    };

    // Render detail panel with nested fields
    const renderDetailPanel = ({ row }) => {
        if (!nestedFieldColumns || nestedFieldColumns.length === 0) {
            return null;
        }

        return (
            <Mui.Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 3,
                    padding: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? '#444' : '#f5f5f5',
                    flexWrap: 'wrap',
                }}
            >
                {nestedFieldColumns.map((groupedCol) => {
                    // Get the data using the parent field name (e.g., "commentCodes")
                    const value = row.original[groupedCol.parentField];

                    if (!value || (Array.isArray(value) && value.length === 0)) {
                        return null;
                    }

                    const fieldMap = new Map();
                    groupedCol.originalColumns.forEach(col => {
                        const propertyName = col.field.split('.')[1];
                        if (propertyName) {
                            const displayName = col.detailHeaderName || col.headerName;
                            fieldMap.set(propertyName, displayName);
                        }
                    });

                    return (
                        <Mui.Box key={groupedCol.parentField} sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                            <Mui.Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                {addSpacesToCamelCase(groupedCol.headerName)}:
                            </Mui.Typography>
                            {Array.isArray(value) ? (
                                // Display array of objects as a list
                                <Mui.Box sx={{ pl: 2 }}>
                                    {value.map((item, idx) => (
                                        <Mui.Box
                                            key={idx}
                                            sx={{
                                                mb: idx < value.length - 1 ? 1.5 : 0,
                                                pb: idx < value.length - 1 ? 1.5 : 0,
                                                borderBottom: idx < value.length - 1 ? '1px solid' : 'none',
                                                borderColor: theme.palette.mode === 'dark' ? '#555' : '#ddd'
                                            }}
                                        >
                                            {typeof item === 'object' && item !== null ? (
                                                groupedCol.originalColumns.map(col => {
                                                    const key = col.field.split('.')[1];
                                                    if (!key) return null;
                                                    const headerName = fieldMap.get(key);
                                                    if (!headerName) return null;
                                                    const val = item[key];
                                                    return (
                                                        <Mui.Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                                                            <strong>{headerName}:</strong> {val !== null && val !== undefined ? String(val) : '-'}
                                                        </Mui.Typography>
                                                    );
                                                })
                                            ) : (
                                                <Mui.Typography variant="body2">
                                                    {String(item)}
                                                </Mui.Typography>
                                            )}
                                        </Mui.Box>
                                    ))}
                                </Mui.Box>
                            ) : typeof value === 'object' && value !== null ? (
                                // Display single object
                                <Mui.Box sx={{ pl: 2 }}>
                                    {groupedCol.originalColumns.map(col => {
                                        const key = col.field.split('.')[1];
                                        if (!key) return null;
                                        const headerName = fieldMap.get(key);
                                        if (!headerName) return null;
                                        const val = value[key];
                                        return (
                                            <Mui.Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                                                <strong>{headerName}:</strong> {val !== null && val !== undefined ? String(val) : '-'}
                                            </Mui.Typography>
                                        );
                                    })}
                                </Mui.Box>
                            ) : (
                                // Display primitive value
                                <Mui.Typography variant="body2" sx={{ pl: 2 }}>
                                    {String(value)}
                                </Mui.Typography>
                            )}
                        </Mui.Box>
                    );
                })}
            </Mui.Box>
        );
    };

    const handleChangeRequested = () => {
        setOpenProgramDialog(true);
    };

    const handleExport = async () => {
        try {
            if (!searchParams) {
                console.error('No search parameters available');
                alert('No search results to export');
                return;
            }

            // Use exportGrid mutation with criteria for advanced search
            const exportQueryVariables = {
                input: {
                    criteria: searchParams.criteria,
                    reportId: "advancedSearch"
                }
            };

            const result = await triggerExport(exportQueryVariables).unwrap();

            // Store the receiptId to track this pending export
            if (result?.receiptId) {
                setPendingExportReceiptId(result.receiptId);
                console.log('Export initiated successfully. The file will open automatically when ready.', result.receiptId);
            }
        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export search results. Please try again.');
        }
    };

    const firstSelectedIndex = Object.entries(selectedRows).find(([_index, isSelected]) => isSelected)?.[0];
    const firstSelectedApplicant = tableData[firstSelectedIndex];

    const disableActionsButton = useMemo(() => {
        const criteria = searchParams?.criteria ?? [];
        if (!criteria.length) return false;

        const statusCriteria = criteria.filter(({ attribute }) => attribute === "status");
        if (!statusCriteria.length) return false;

        return statusCriteria.some(({ operator, value }) => {
            const parsed = JSON.parse(value);
            const values = Array.isArray(parsed.value) ? parsed.value : [parsed.value];

            if (operator === 'EQ' || operator === 'CNT') {
                return values.every(
                    (v) => v === APPLICATION_STATUS.COMPLETE || v === APPLICATION_STATUS.CANCELLED
                );
            }

            return false;
        });
    }, [searchParams?.criteria]);

    return (
        <>
            <MaterialReactTable
                enableStickyHeader={true}
                columns={mappedColumns}
                data={tableData}
                enableColumnResizing
                enableColumnPinning
                enableColumnOrdering
                enableColumnDragging
                // enableRowPinning
                enableSorting
                enableFilters={false}
                enableSearch={false}
                enableExpanding={nestedFieldColumns.length > 0}
                manualPagination={true}
                manualSorting={true}
                renderDetailPanel={nestedFieldColumns.length > 0 ? renderDetailPanel : undefined}
                state={{
                    pagination: paginationModel,
                    sorting: sortModel,
                    isLoading,
                    columnVisibility,
                    ...(columnOrder.length > 0 && { columnOrder }),
                    rowSelection: selectedRows,
                }}
                rowCount={resultData?.totalCount || 0}
                pageCount={Math.ceil((resultData?.totalCount || 0) / (paginationModel?.pageSize || 100))}
                onPaginationChange={setPaginationModel}
                onSortingChange={setSortModel}
                onColumnVisibilityChange={persistColumnVisibility}
                onColumnOrderChange={(newColumnOrder) => {
                    setColumnOrder(currentOrder => {
                        const resolved = typeof newColumnOrder === 'function' ? newColumnOrder(currentOrder) : newColumnOrder;
                        const allowedIds = new Set(defaultColumnOrder);
                        const filtered = (resolved || []).filter((id) => allowedIds.has(id));
                        const missing = defaultColumnOrder.filter((id) => !filtered.includes(id));
                        const nextOrder = filtered.length > 0 ? [...filtered, ...missing] : defaultColumnOrder;

                        const columnSetUnchanged = defaultColumnOrder.join(',') === previousColumnIdsRef.current.join(',');
                        const orderChanged = JSON.stringify(nextOrder) !== JSON.stringify(previousColumnOrderRef.current);

                        if (columnSetUnchanged && orderChanged) {
                            const saved = localStorage.getItem(storageKey);
                            const existing = saved ? JSON.parse(saved) : {};
                            if (filtered.length === 0 || JSON.stringify(nextOrder) === JSON.stringify(defaultColumnOrder)) {
                                if (existing.columnOrder) {
                                    delete existing.columnOrder;
                                    if (Object.keys(existing).length === 0) {
                                        localStorage.removeItem(storageKey);
                                    } else {
                                        localStorage.setItem(storageKey, JSON.stringify(existing));
                                    }
                                }
                            } else {
                                localStorage.setItem(storageKey, JSON.stringify({ ...existing, columnOrder: nextOrder }));
                            }
                        }

                        previousColumnOrderRef.current = nextOrder;
                        previousColumnIdsRef.current = defaultColumnOrder;
                        return nextOrder;
                    });
                }}
                enableRowSelection={isPersonSearch}
                enableMultiRowSelection={false}
                onRowSelectionChange={(newRowSelection) => {
                    setSelectedRows(newRowSelection || {});
                }}
                muiPaginationProps={{
                    rowsPerPageOptions: [10, 25, 50, 100],
                    showFirstButton: true,
                    showLastButton: true,
                }}
                muiTableContainerProps={{
                    sx: {
                        maxHeight: '40rem', // Set a fixed max height
                        overflow: 'auto',    // Enable scrolling
                    }
                }}
                muiTablePaperProps={{
                    sx: sharedTableStyles,
                }}
                muiTableHeadProps={{
                    sx: sharedTableStyles,
                }}
                muiTableBodyProps={{
                    sx: sharedTableStyles,
                }}
                muiTopToolbarProps={{
                    sx: sharedTableStyles,
                }}
                muiTableHeadCellProps={{
                    sx: sharedTableStyles,
                }}
                muiTableBodyCellProps={{
                    sx: sharedTableStyles,
                }}
                muiBottomToolbarProps={{
                    sx: sharedTableStyles,
                }}
                muiEditRowDialogProps={{
                    sx: sharedTableStyles,
                }}
                renderToolbarInternalActions={({ table }) => (
                    <ToolbarInternalActionsWithReset
                        table={table}
                        onResetColumns={() => {
                            const resetVisibility = defaultColumnVisibilityRef.current || defaultColumnVisibility || {};
                            setColumnVisibility(resetVisibility);

                            const saved = localStorage.getItem(storageKey);
                            if (saved) {
                                try {
                                    const existing = JSON.parse(saved);
                                    if (existing.columnVisibility) {
                                        delete existing.columnVisibility;
                                        if (Object.keys(existing).length === 0) {
                                            localStorage.removeItem(storageKey);
                                        } else {
                                            localStorage.setItem(storageKey, JSON.stringify(existing));
                                        }
                                    }
                                } catch (err) {
                                    console.warn('Failed to parse saved settings during reset');
                                }
                            }

                            initialColumnVisibilityRef.current = resetVisibility;
                        }}
                    />
                )}

                renderTopToolbarCustomActions={() => (
                    <Mui.Box sx={{ display: 'flex', alignItems: 'center', width: '100%', px: 1 }}>
                        {isPersonSearch ? (
                            <>
                                {/* Left: Clear Selection */}
                                <Mui.Box>
                                    <Mui.Button
                                        color="secondary"
                                        variant="outlined"
                                        size="small"
                                        startIcon={<MuiIcons.Clear />}
                                        onClick={handleClearSelection}
                                        disabled={!tableData || tableData.length === 0 || Object.keys(selectedRows).length === 0}
                                    >
                                        Clear Selection
                                    </Mui.Button>
                                </Mui.Box>

                                {/* Spacer */}
                                <Mui.Box sx={{ flex: 1 }} />

                                {/* Right: Request Change */}
                                <Mui.Box>
                                    <Mui.Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleChangeRequested}
                                        disabled={!firstSelectedApplicant}
                                    >
                                        Request Change
                                    </Mui.Button>
                                </Mui.Box>
                            </>
                        ) : (
                            <Mui.Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Mui.Button
                                    color="info"
                                    variant="outlined"
                                    size="small"
                                    startIcon={<MuiIcons.Download />}
                                    onClick={handleExport}
                                    disabled={isExporting || !tableData || tableData.length === 0 || pendingExportReceiptId !== null}
                                >
                                    {isExporting || pendingExportReceiptId !== null ? "Exporting..." : "Export to CSV"}
                                </Mui.Button>
                                {isAdmin && (
                                    <>
                                        <Mui.Button
                                            variant="contained"
                                            size="small"
                                            endIcon={<ArrowDropDownIcon />}
                                            onClick={handleDropdownClick}
                                            disabled={!tableData || tableData.length === 0 || disableActionsButton}
                                        >
                                            Actions
                                        </Mui.Button>
                                        <Mui.Menu
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl)}
                                            onClose={handleDropdownClose}
                                        >
                                            <Mui.MenuItem onClick={handleChangeWorkflow}>Change Workflow</Mui.MenuItem>
                                            <Mui.MenuItem onClick={handleOverride}>Administrative Recommendation</Mui.MenuItem>
                                        </Mui.Menu>
                                    </>
                                )}
                            </Mui.Box>
                        )}
                    </Mui.Box>
                )}

                sx={{
                    width: "100%",
                    backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#fff',
                }}
                initialState={{
                    density: 'compact'
                }}
            />
            <ChangeWorkflowDialog
                openDialog={openDialog}
                setOpenDialog={setOpenDialog}
                searchParams={searchParams}
                clearSearchCriteria={clearSearchCriteria}
                clearGrid={clearGrid}
                skip={!authDetails.isAnyAdmin}
                totalCount={resultData?.totalCount}
            />
            <DialogAdminOverride
                openDialog={openOverrideDialog}
                setOpenDialog={setOpenOverrideDialog}
                searchParams={searchParams}
                clearSearchCriteria={clearSearchCriteria}
                clearGrid={clearGrid}
                skip={!authDetails.isAnyAdmin}
                totalCount={resultData?.totalCount}
            />
            <ChangeRequestedDialog
                openDialog={openProgramDialog}
                setOpenDialog={setOpenProgramDialog}
                searchParams={searchParams}
                applicant={firstSelectedApplicant}
                isAdmin={isAdmin}
            />
        </>
    );
}

export default React.memo(ResultsGrid);
